import {
  MapPin,
  Calendar,
  ArrowLeft,
  ArrowRight,
  Home,
  Tag,
} from "lucide-react";
import { useState } from "react";

import house21 from "../assets/house2/1.jpg";
import house22 from "../assets/house2/2.jpg";
import house23 from "../assets/house2/3.jpg";
import house31 from "../assets/house3/1.jpg";
import house41 from "../assets/house4/1.jpeg";

import building1 from "../assets/building1/1.jpg";
import building2 from "../assets/building1/2.jpg";
import building3 from "../assets/building1/3.jpg";

export default function RecentBuilds() {
  const [hoveredProject, setHoveredProject] = useState(null);
  const [imageIndex, setImageIndex] = useState({});

  const projects = [
    {
      title: "Республика Адыгея",
      location: "Майкоп",
      year: "2025",
      area: 120,
      price: "от 5,500,000 ₽",
      images: [house31],
      category: "Жилые",
    },
    {
      title: "Республика Адыгея",
      location: "Майкоп",
      year: "2025",
      area: 110,
      price: "от 4,500,000 ₽",
      images: [house21, house22, house23],
      category: "Жилые",
    },
    {
      title: "Республика Адыгея",
      location: "Майкоп",
      year: "2026",
      area: 680,
      price: "от 19,000,000 ₽",
      images: [building1, building2, building3],
      category: "Коммерческие",
    },
    {
      title: "Республика Адыгея",
      location: "ГАВЕРДОВСКИЙ РАЙОН",
      year: "2023",
      area: 95,
      price: "от 7,500,000 ₽",
      images: [house41],
      category: "Жилые",
    },
  ];

  return (
    <section className="bg-white py-24 bg-[#fafaf9]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Section Header */}
        <div className="mb-20">
          <div className="flex items-center gap-4 mb-10">
            <span className="w-10 h-px bg-[#147f8f]" />
            <span className="text-sm font-bold tracking-[0.35em] uppercase text-[#147f8f]">
              Наши проекты
            </span>
          </div>

          <div className="grid lg:grid-cols-2 gap-10 items-end">
            <h2 className="text-4xl md:text-5xl lg:text-6xl leading-[1.08] tracking-[-0.04em] text-slate-950">
              <span className="font-semibold block">
                Недавние
              </span>
              <span className="font-light text-slate-500 block">
                строительные проекты.
              </span>
            </h2>

            <p className="text-lg md:text-xl leading-[1.8] text-slate-600 max-w-2xl lg:pb-3">
              Ознакомьтесь с нашими последними проектами, где качество
              строительства, современные решения и внимание к деталям объединены
              в каждом объекте.
            </p>
          </div>
        </div>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 gap-8">
          {projects.map((project, index) => {
            const currentImageIndex = imageIndex[index] || 0;

            return (
              <div
                key={index}
                className="group bg-white rounded-sm overflow-hidden border border-slate-200 shadow-sm hover:shadow-2xl transition-all duration-300"
                onMouseEnter={() => {
                  setHoveredProject(index);
                  setImageIndex({ ...imageIndex, [index]: 0 });
                }}
                onMouseLeave={() => setHoveredProject(null)}
              >
                <div className="relative h-[21rem] overflow-hidden cursor-pointer">
                  
                  {/* Left Navigation */}
                  {project.images.length > 1 && (
                    <button
                      type="button"
                      className="absolute left-0 top-0 w-[12%] h-full z-20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => {
                        const prevIndex = currentImageIndex - 1;

                        setImageIndex({
                          ...imageIndex,
                          [index]:
                            prevIndex < 0
                              ? project.images.length - 1
                              : prevIndex,
                        });
                      }}
                    >
                      <ArrowLeft className="w-8 h-8 text-white" />
                    </button>
                  )}

                  {/* Right Navigation */}
                  {project.images.length > 1 && (
                    <button
                      type="button"
                      className="absolute right-0 top-0 w-[12%] h-full z-20 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                      onClick={() => {
                        const nextIndex = currentImageIndex + 1;

                        setImageIndex({
                          ...imageIndex,
                          [index]: nextIndex % project.images.length,
                        });
                      }}
                    >
                      <ArrowRight className="w-8 h-8 text-white" />
                    </button>
                  )}

                  {/* Overlay */}
                  <div className="absolute inset-0 bg-black/10 z-[5]" />

                  <img
                    src={project.images[currentImageIndex]}
                    alt={project.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />

                  <div className="absolute top-4 right-4 z-10 bg-primaryBlue text-white px-4 py-2 rounded-sm text-sm font-semibold">
                    {project.category}
                  </div>

                  {/* Slide Indicators */}
                  {project.images.length > 1 && (
                    <div className="absolute bottom-0 left-0 right-0 z-10 flex gap-2 p-4 justify-center">
                      {project.images.map((_, slideIndex) => (
                        <div
                          key={slideIndex}
                          className={`h-2 rounded-sm transition-all ${
                            slideIndex === currentImageIndex
                              ? "w-6 bg-[#76bb17]"
                              : "w-2 bg-white"
                          }`}
                        />
                      ))}
                    </div>
                  )}
                </div>

                <div className="p-6">
                  <h3 className="text-2xl font-bold text-slate-900 mb-4 group-hover:text-primaryBlue transition-colors">
                    {project.title}
                  </h3>

                  <div className="flex flex-wrap items-center gap-4 text-gray-600">
                    <div className="flex items-center gap-2">
                      <MapPin className="w-4 h-4" />
                      <span className="text-sm">{project.location}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      <span className="text-sm">{project.year}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Home className="w-4 h-4" />
                      <span className="text-sm">{project.area} м²</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Tag className="w-4 h-4" />
                      <span className="text-sm font-semibold">
                        {project.price}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

      </div>
    </section>
  );
}